
import { Component, Ref, Vue } from "vue-property-decorator";
import DeviceCategoryItem from "@/components/allDevices/DeviceCategoryItem.vue";
import {
  CATEGORY_HY_DRY_ID,
  CATEGORY_COM_BOX_ID,
  getCategoryIdFromSerialNumber,
  isIresCategory,
  isUnknownCategory,
  removeCategory,
} from "@/data/models/DeviceSettingsManager";
import { DevicesModule } from "@/store/modules/DevicesModule";
import { observeDeviceOverviews } from "@/data/repository/AllDevicesRepository";
import { DeviceCategoryTemplate } from "@/data/models/DeviceCategoryTemplate";
import { GenericDeviceFilter } from "@/data/models/DeviceFilter";
import { getIresDeviceCategory } from "@/data/models/IresDevice";
import { setNumber } from "@/utils/SecureLocalStorage";
import router from "@/router/router";
import ImportHelpDialog from "@/components/settings/devices/import/ImportHelpDialog.vue";
import EditCategoryDialog from "@/components/settings/devices/categories/EditCategoryDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import ConfirmationDialog from "@/components/common/ConfirmationDialog.vue";
import ClientPanel from "@/data/models/ClientPanel";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import { filterRetainedDevices, getNumberOfRetainedDevices } from "@/utils/DeviceManagementUtils";
import { observeClientPanels } from "@/data/repository/ClientPanelRepository";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { editIresCategoriesAndTemplatesAccess } from "@/permissions/UserPermissions";
import { getActiveUser } from "@/data/repository/UserRepository";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { StateChanger } from "vue-infinite-loading";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";

@Component({
  computed: {
    GenericDeviceFilter() {
      return GenericDeviceFilter;
    },
  },
  methods: { isIresCategory, getIresDeviceCategory },
  components: { ConfirmationDialog, ImportHelpDialog, DeviceCategoryItem, EditCategoryDialog },
})
export default class DeviceCategoryList extends Vue {
  infiniteId = new Date();
  priorityIds = [CATEGORY_COM_BOX_ID, CATEGORY_HY_DRY_ID];
  pageSize = 20;
  sliceLength = this.pageSize;
  storageKeySliceLength = "DeviceCategoryStorageKeySliceLength";
  subscriptions: Array<() => void> = [];
  componentActive = false;
  errorSnackbar = false;
  errorSnackbarText = "";
  successSnackbar = false;
  successSnackbarText = "";

  retrievedClientPanels: ClientPanel[] = [];
  retrieveDeviceOverviews: DeviceOverview[] = [];

  /** Table header background, theme-aware so it stays readable in dark mode. */
  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  get showInfiniteLoading() {
    return this.deviceCategories.length > 0;
  }
  
  @Ref("editDialog")
  editCategoryDialog!: EditCategoryDialog;
  
  @Ref("confirmDialog")
  confirmationDialog!: ConfirmationDialogComponent;

  async created() {
    this.subscriptions.push(observeDeviceOverviews(
      (deviceOverview: Array<DeviceOverview>) => {
        this.retrieveDeviceOverviews = deviceOverview;
      }
    ));
    this.subscriptions.push(observeClientPanels(
      (clientPanels: Array<ClientPanel>) => {
        this.retrievedClientPanels = clientPanels;
      }
    ));

    this.componentActive = true;
  }

  destroyed() {
    this.componentActive = false;
  }

  resetDisplayedCategories() {
    this.sliceLength = this.pageSize;
    this.infiniteId = new Date();
  }

  sliceCategoriesToDisplay(length: number) {
    const deviceCategories = this.deviceCategories;

    return deviceCategories.slice(0, length);
  }

  get categoriesToDisplay() {
    return this.sliceCategoriesToDisplay(this.sliceLength);
  }

  get deviceCategories() {
    const priorityIds = this.priorityIds;
    const deviceSettingsJson = this.deviceSettingsJson;

    return deviceSettingsJson
      ? deviceSettingsJson.deviceCategories.sort((a, b) => {
        if (priorityIds.includes(a.id) && priorityIds.includes(b.id)) {
          return priorityIds.indexOf(a.id) - priorityIds.indexOf(b.id);
        } else if (priorityIds.includes(a.id)) {
          return -1;
        } else if (priorityIds.includes(b.id)) {
          return 1;
        } else {
          return a.categoryName.localeCompare(b.categoryName);
        }
      })
      : [];
  }

  getDevicesWithCategory(deviceCategoryId: string) {
    if (isIresCategory(deviceCategoryId)) {
      const clientPanels = this.clientPanels.filter((panel) => {
        return getCategoryIdFromSerialNumber(panel.serialNumber) === deviceCategoryId;
      });

      return filterRetainedDevices(clientPanels);
    }
    
    const deviceOverviews = this.deviceOverviews.filter((device) => {
      return device.categoryId === deviceCategoryId;
    });

    return filterRetainedDevices(deviceOverviews);
  }

  getNoOfTotalDevices(devices: (DeviceOverview | ClientPanel)[]) {
    return getNumberOfRetainedDevices(devices);
  }

  onOpenDeviceCategory(categoryId: string, index: number) {
    setNumber(
      this.storageKeySliceLength,
      this.pageSize + Math.floor(index / this.pageSize) * this.pageSize,
    );
    const selectedCategory = this.deviceCategories.filter((category) => {
      return category.id === categoryId;
    });
    DevicesModule.setSelectedCategories(selectedCategory);
    router.push({
      path: `deviceCategories/${categoryId}`,
      params: { categoryId: categoryId },
    });
  }

  get clientPanels() {
    return this.retrievedClientPanels;
  }

  get deviceOverviews() {
    return this.retrieveDeviceOverviews;
  }

  get selectedDeviceFilter() {
    return GenericDeviceFilter.ALL_DEVICES;
  }

  openNewCategoryDialog() {
    this.editCategoryDialog.show("", true);
  }

  onEditCategory(id: string) {
    this.editCategoryDialog.show(this.categoriesToDisplay.find(cat => cat.id === id)?.categoryName ?? "", false);
  }

  async infiniteHandler(data: StateChanger) {
    const nextSliceLength = this.sliceLength + this.pageSize;
    const nextSlice = this.sliceCategoriesToDisplay(nextSliceLength);

    this.sliceLength = nextSliceLength;

    if (nextSliceLength > nextSlice.length) {
      data.complete();
    } else {
      data.loaded();
    }
  }

  async onDeleteCategory(id: string) {
    const category = this.deviceCategories.find(cat => cat.id == id);
    if (!category) {
      return;
    }
    const categoryName = category?.categoryName;
    const result = await this.confirmationDialog.show({
      title: this.$t("allDevices.deleteCategoryConfirmTitle", [categoryName]).toString(),
      message: this.$t("allDevices.deleteCategoryConfirmMessage", [categoryName]).toString(),
      acceptMessage: this.$t("common.delete").toString(),
    });
    if (result && this.isRemovable(category)) {
      await removeCategory(id, this.deviceSettingsJson);
      
      this.showSuccessSnackbar("allDevices.deviceCategoryDeleted");
      this.resetDisplayedCategories()
    }
  }

  showErrorSnackbar(message: string) {
    this.errorSnackbar = true;
    this.errorSnackbarText = message;
  }

  showSuccessSnackbar(message: string) {
    this.successSnackbar = true;
    this.successSnackbarText = message;
  }

  onError(data: string) {
    this.showErrorSnackbar(data);
  }

  onSuccess(data: string) {
    this.resetDisplayedCategories();
    this.showSuccessSnackbar(data);
  }

  isRemovable(deviceCategory: DeviceCategoryTemplate) {
    return this.getDevicesWithCategory(deviceCategory.id).length <= 0 && !isIresCategory(deviceCategory.id);
  }

  isIresCategory(categoryId: string) {
    return isIresCategory(categoryId);
  }

  isUnknownCategory(categoryId: string) {
    return isUnknownCategory(categoryId);
  }

  mounted() {
    this.setToolbarTitle();
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const currentPageTitle = this.$t("settings.hyDryDevice.devicesCategorySettings").toString();

    const toolbarTitle = baseTitle + " > " + currentPageTitle;

    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }

  get isAllowedToEditOrDeleteCategory() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get isAllowedToEditIresCategoriesAndTemplates() {
    return editIresCategoriesAndTemplatesAccess(getActiveUser());
  }
}
